import { Search } from "lucide-react";
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

const disasterIconConfigs = {
  FLOOD: { color: "#3B82F6", icon: "💧", label: "Flood" },
  Flood: { color: "#3B82F6", icon: "💧", label: "Flood" },
  COLD_WAVE: { color: "#6B7280", icon: "❄️", label: "Cold Wave" },
  "Cold Wave": { color: "#6B7280", icon: "❄️", label: "Cold Wave" },
  EARTHQUAKE: { color: "#8B5CF6", icon: "🌍", label: "Earthquake" },
  Earthquake: { color: "#8B5CF6", icon: "🌍", label: "Earthquake" },
  FIRE: { color: "#DC2626", icon: "🔥", label: "Fire" },
  Fire: { color: "#DC2626", icon: "🔥", label: "Fire" },
  CYCLONE: { color: "#F59E0B", icon: "🌀", label: "Cyclone" },
  Cyclone: { color: "#F59E0B", icon: "🌀", label: "Cyclone" },
  "Heat Wave": { color: "#F97316", icon: "🔥", label: "Heat Wave" },
  HEAT_WAVE: { color: "#F97316", icon: "🔥", label: "Heat Wave" },
  Drought: { color: "#D97706", icon: "🌵", label: "Drought" },
  DROUGHT: { color: "#D97706", icon: "🌵", label: "Drought" },
  Landslide: { color: "#92400E", icon: "⛰️", label: "Landslide" },
  LANDSLIDE: { color: "#92400E", icon: "⛰️", label: "Landslide" },
  Tsunami: { color: "#0891B2", icon: "🌊", label: "Tsunami" },
  TSUNAMI: { color: "#0891B2", icon: "🌊", label: "Tsunami" },
  Tornado: { color: "#7C3AED", icon: "🌪️", label: "Tornado" },
  TORNADO: { color: "#7C3AED", icon: "🌪️", label: "Tornado" },
  Avalanche: { color: "#E0E7FF", icon: "🏔️", label: "Avalanche" },
  AVALANCHE: { color: "#E0E7FF", icon: "🏔️", label: "Avalanche" },
  Wildfire: { color: "#DC2626", icon: "🔥", label: "Wildfire" },
  WILDFIRE: { color: "#DC2626", icon: "🔥", label: "Wildfire" },
  DEFAULT: { color: "#6B7280", icon: "⚠️", label: "Incident" },
};

const placesIconConfigs = {
  'hospital': { color: '#2563eb', emoji: '🏥', label: 'Hospital', bgColor: '#eff6ff' },
  'pharmacy': { color: '#10b981', emoji: '💊', label: 'Pharmacy', bgColor: '#f0fdf4' },
  'fire_station': { color: '#dc2626', emoji: '🚒', label: 'Fire Station', bgColor: '#fef2f2' },
  'police': { color: '#3b82f6', emoji: '👮', label: 'Police Station', bgColor: '#eff6ff' },
  'gas_station': { color: '#059669', emoji: '⛽', label: 'Fuel Station', bgColor: '#ecfdf5' },
  'bank': { color: '#d97706', emoji: '🏦', label: 'Bank / ATM', bgColor: '#fffbeb' },
  'atm': { color: '#d97706', emoji: '🏧', label: 'Bank / ATM', bgColor: '#fffbeb' },
  'school': { color: '#f59e0b', emoji: '🏫', label: 'School (Shelter)', bgColor: '#fffbeb' },
  'lodging': { color: '#7c3aed', emoji: '🏨', label: 'Hotel (Refuge)', bgColor: '#f5f3ff' },
  'grocery_or_supermarket': { color: '#16a34a', emoji: '🛒', label: 'Grocery / Supply', bgColor: '#f0fdf4' },
  'supermarket': { color: '#16a34a', emoji: '🛒', label: 'Grocery / Supply', bgColor: '#f0fdf4' },
  'local_government_office': { color: '#8b5cf6', emoji: '🏛️', label: 'Govt. Office', bgColor: '#f5f3ff' },
  'helipad': { color: '#0ea5e9', emoji: '🚁', label: 'Helipad', bgColor: '#f0f9ff' },
  'DEFAULT': { color: '#6b7280', emoji: '📍', label: 'Place', bgColor: '#f9fafb' },
};

const ALWAYS_VISIBLE_DISASTERS = ['Earthquake', 'Flood', 'Avalanche'];

const extractIncidentType = (incidentName) => {
  if (!incidentName) return 'DEFAULT';
  const parts = incidentName.split('_');
  let possibleTypes = [parts[0]];
  if (parts.length >= 2) {
    possibleTypes.push(parts[0] + '_' + parts[1]);
    possibleTypes.push(parts[0] + ' ' + parts[1]);
  }
  const locationTimePattern = /_(?:(?!_)[A-Z]){2,}_\d+_\d+/;
  const baseName = incidentName.replace(locationTimePattern, '');
  if (baseName !== incidentName) possibleTypes.push(baseName);

  const typeMapping = {
    'Earthquake': 'Earthquake', 'EARTHQUAKE': 'Earthquake', 'earthquake': 'Earthquake',
    'Cold Wave': 'Cold Wave', 'Cold_Wave': 'Cold Wave', 'COLD_WAVE': 'Cold Wave', 'ColdWave': 'Cold Wave', 'coldwave': 'Cold Wave',
    'Flood': 'Flood', 'FLOOD': 'Flood', 'flood': 'Flood',
    'Fire': 'Fire', 'FIRE': 'Fire', 'fire': 'Fire',
    'Cyclone': 'Cyclone', 'CYCLONE': 'Cyclone', 'cyclone': 'Cyclone',
    'Heat Wave': 'Heat Wave', 'Heat_Wave': 'Heat Wave', 'HEAT_WAVE': 'Heat Wave', 'HeatWave': 'Heat Wave', 'heatwave': 'Heat Wave',
    'Drought': 'Drought', 'DROUGHT': 'Drought', 'drought': 'Drought',
    'Landslide': 'Landslide', 'LANDSLIDE': 'Landslide', 'landslide': 'Landslide',
    'Tsunami': 'Tsunami', 'TSUNAMI': 'Tsunami', 'tsunami': 'Tsunami',
    'Tornado': 'Tornado', 'TORNADO': 'Tornado', 'tornado': 'Tornado',
    'Avalanche': 'Avalanche', 'AVALANCHE': 'Avalanche', 'avalanche': 'Avalanche',
    'Wildfire': 'Wildfire', 'WILDFIRE': 'Wildfire', 'wildfire': 'Wildfire'
  };
  for (const possibleType of possibleTypes) {
    if (typeMapping[possibleType]) return typeMapping[possibleType];
  }
  return 'DEFAULT';
};

const isValidCoordinate = (lat, lng) => {
  const latitude = parseFloat(lat);
  const longitude = parseFloat(lng);
  return !isNaN(latitude) && !isNaN(longitude) &&
    latitude >= -90 && latitude <= 90 &&
    longitude >= -180 && longitude <= 180;
};

const ActivePanelMapLegend = ({
  mapRef,
  topOffset = 10,
  isInfoWindowOpen,
  selectedIncident,
  allIncidentsData,
  liveVehicleData,
  resourceData,
  visibleResources,
  nearbyPlacesData,
  hospitalData,
  showVehicles,
  showIncidents,
  showIncidentMarkers,
  showNearbyPlaces,
  showAllLayers,
  setShowAllLayers,
  selectedLegendFilters,
  setSelectedLegendFilters,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeItem, setActiveItem] = useState(null);
  const [isComputingCounts, setIsComputingCounts] = useState(false);

  // Once the user manually checks/unchecks an item we stop auto-hydrating,
  // so their choices are preserved when new places load in.
  const userHasInteracted = useRef(false);


  const effectiveExpanded = isInfoWindowOpen ? false : isExpanded;

  // ─── Build legend items ──────────────────────────────────────────────────

  const legendItems = useMemo(() => {
    const items = [];
    const incidents = selectedIncident === "all"
      ? allIncidentsData
      : selectedIncident && selectedIncident !== "all"
        ? [selectedIncident]
        : [];

    // 1. Incident disaster markers (grouped by type)
    if (showIncidentMarkers && incidents.length > 0) {
      const incidentGroups = {};
      incidents.forEach(incident => {
        if (incident.incidentLocation) {
          try {
            const location = JSON.parse(incident.incidentLocation);
            if (location.incidentcenter && Array.isArray(location.incidentcenter)) {
              const [lng, lat] = location.incidentcenter;
              if (isValidCoordinate(lat, lng)) {
                const type = extractIncidentType(incident.incidentName);
                const config = disasterIconConfigs[type] || disasterIconConfigs.DEFAULT;
                if (!incidentGroups[config.label]) {
                  incidentGroups[config.label] = {
                    type: 'incident', key: `incident-${config.label}`,
                    emoji: config.icon, color: config.color, label: config.label,
                    baseLabel: config.label, borderColor: config.color, positions: []
                  };
                }
                incidentGroups[config.label].positions.push([parseFloat(lat), parseFloat(lng)]);
              }
            }
          } catch (e) { }
        }
      });
      Object.values(incidentGroups).forEach(group => {
        items.push({ ...group, label: `${group.baseLabel} (${group.positions.length})` });
      });
    }

    // 2. Live vehicles
    if (showVehicles) {
      const vehiclePositions = [];
      if (resourceData && liveVehicleData?.length > 0) {
        liveVehicleData.forEach(vehicle => {
          if (isValidCoordinate(vehicle.latitude, vehicle.longitude))
            vehiclePositions.push([parseFloat(vehicle.latitude), parseFloat(vehicle.longitude)]);
        });
      } else if (!resourceData && incidents.length > 0) {
        incidents.forEach(incident => {
          incident.resources?.forEach(resource => {
            resource.vehicles?.forEach(vehicle => {
              if (isValidCoordinate(vehicle.latitude, vehicle.longitude))
                vehiclePositions.push([parseFloat(vehicle.latitude), parseFloat(vehicle.longitude)]);
            });
          });
        });
      }
      if (vehiclePositions.length > 0) {
        items.push({
          type: 'vehicle', key: 'vehicle-ambulance', emoji: '🚑',
          color: 'rgba(197, 194, 194, 0.4)', label: `Live Vehicles (${vehiclePositions.length})`,
          baseLabel: 'Live Vehicles', borderColor: 'white', positions: vehiclePositions
        });
      }
    }

    // 3. Facilities
    if (showIncidents && incidents.length > 0) {
      const facilityPositions = [];
      // When navigated from DeploymentList, only count/show the specific deployed resource's facility
      const resourcesToCount = resourceData && visibleResources?.length > 0
        ? visibleResources
        : null;
      if (resourcesToCount) {
        resourcesToCount.forEach(resource => {
          if (isValidCoordinate(resource.facilityLatitude, resource.facilityLongitude))
            facilityPositions.push([parseFloat(resource.facilityLatitude), parseFloat(resource.facilityLongitude)]);
        });
      } else {
        incidents.forEach(incident => {
          incident.resources?.forEach(resource => {
            if (isValidCoordinate(resource.facilityLatitude, resource.facilityLongitude))
              facilityPositions.push([parseFloat(resource.facilityLatitude), parseFloat(resource.facilityLongitude)]);
          });
        });
      }
      if (facilityPositions.length > 0) {
        items.push({
          type: 'facility', key: 'facility-marker', isImage: true,
          color: 'white', label: `Facilities (${facilityPositions.length})`,
          baseLabel: 'Facilities', borderColor: 'white', positions: facilityPositions
        });
      }
    }

    // 4. Nearby Google Places (grouped by place type)
    if (showNearbyPlaces && nearbyPlacesData?.length > 0) {
      const placeGroups = {};
      nearbyPlacesData.forEach(place => {
        if (isValidCoordinate(place.latitude, place.longitude)) {
          const config = placesIconConfigs[place.layerType] || placesIconConfigs.DEFAULT;
          const groupKey = place.layerType || 'DEFAULT';
          if (!placeGroups[groupKey]) {
            placeGroups[groupKey] = {
              type: 'nearby_place', key: `nearby-place-${groupKey}`,
              emoji: config.emoji, color: config.color, label: config.label,
              baseLabel: config.label, borderColor: config.color,
              bgColor: config.bgColor, positions: []
            };
          }
          placeGroups[groupKey].positions.push([parseFloat(place.latitude), parseFloat(place.longitude)]);
        }
      });
      Object.values(placeGroups).forEach(group => {
        items.push({ ...group, label: `${group.baseLabel} (${group.positions.length})` });
      });
    }

    // 5. Hospitals
    if (hospitalData?.length > 0) {
      const hospitalPositions = hospitalData
        .filter(h => isValidCoordinate(h.latitude, h.longitude))
        .map(h => [parseFloat(h.latitude), parseFloat(h.longitude)]);
      if (hospitalPositions.length > 0) {
        items.push({
          type: 'hospital', key: 'hospital-nearby', emoji: '🏥',
          color: '#2563eb', label: `Nearby Hospitals (${hospitalPositions.length})`,
          baseLabel: 'Nearby Hospitals', borderColor: '#2563eb', positions: hospitalPositions
        });
      }
    }

    return items;
  }, [selectedIncident, allIncidentsData, liveVehicleData, resourceData, visibleResources, nearbyPlacesData,
    hospitalData, showVehicles, showIncidents, showIncidentMarkers, showNearbyPlaces]);


  const handleShowAllToggle = () => {
    setIsComputingCounts(true);
    setTimeout(() => {
      setShowAllLayers(!showAllLayers);
      // your existing filter sync logic here
      setIsComputingCounts(false);
    }, 80);
  };

  // ─── Toggleable items (excludes always-visible disasters) ────────────────

  const toggleableItems = useMemo(
    () => legendItems.filter(
      item => !(item.type === 'incident' && ALWAYS_VISIBLE_DISASTERS.includes(item.baseLabel))
    ),
    [legendItems]
  );

  // ─── Auto-hydrate filters when new legend groups arrive ──────────────────

  // Only Facilities and Live Vehicles should be checked by default; nearby
  // places (Hospital, Fuel Station, etc.) and any other toggleable items
  // start unchecked until the user opts in.
  const DEFAULT_CHECKED_TYPES = ['facility', 'vehicle'];

  useEffect(() => {
    if (legendItems.length === 0) return;
    if (userHasInteracted.current) return; // respect manual choices

    const missingItems = toggleableItems.filter(
      item => DEFAULT_CHECKED_TYPES.includes(item.type) &&
        !selectedLegendFilters.some(
          f => f.type === item.type && f.label === item.baseLabel
        )
    );

    if (missingItems.length === 0) return; // nothing new to add

    const updated = [
      ...selectedLegendFilters,
      ...missingItems.map(item => ({ type: item.type, label: item.baseLabel })),
    ];

    setSelectedLegendFilters(updated);

    // Re-evaluate showAllLayers with the updated list
    const allCovered = toggleableItems.every(ti =>
      updated.some(f => f.type === ti.type && f.label === ti.baseLabel)
    );
    setShowAllLayers(allCovered);
  }, [legendItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Map pan / zoom on legend label click ────────────────────────────────

  const handleLegendItemClick = useCallback((item) => {
    const map = mapRef?.current;
    if (!map || !item.positions || item.positions.length === 0) return;

    setActiveItem(item.key);
    setTimeout(() => setActiveItem(null), 2000);

    if (item.positions.length === 1) {
      const [lat, lng] = item.positions[0];
      map.panTo({ lat, lng });
      map.setZoom(15);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      item.positions.forEach(([lat, lng]) => bounds.extend({ lat, lng }));
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }
  }, [mapRef]);

  // ─── "Show All" checkbox ─────────────────────────────────────────────────

 const handleAllCheckboxChange = useCallback((checked) => {
  userHasInteracted.current = true;
  setIsComputingCounts(true);
  setTimeout(() => {
    setShowAllLayers(checked);
    if (checked) {
      setSelectedLegendFilters(
        toggleableItems.map(item => ({ type: item.type, label: item.baseLabel }))
      );
    } else {
      setSelectedLegendFilters([]);
      // ← Zoom out to show all always-visible disaster markers
      const map = mapRef?.current;
      if (map) {
        map.setZoom(6);
        const alwaysVisibleItems = legendItems.filter(
          item => item.type === 'incident' && ALWAYS_VISIBLE_DISASTERS.includes(item.baseLabel)
        );
        const allPositions = alwaysVisibleItems.flatMap(item => item.positions);
        if (allPositions.length > 1) {
          const bounds = new window.google.maps.LatLngBounds();
          allPositions.forEach(([lat, lng]) => bounds.extend({ lat, lng }));
          map.fitBounds(bounds, { top: 80, right: 80, bottom: 80, left: 80 });
        } else if (allPositions.length === 1) {
          map.panTo({ lat: allPositions[0][0], lng: allPositions[0][1] });
          map.setZoom(7);
        }
      }
    }
    setIsComputingCounts(false);
  }, 80);
}, [toggleableItems, legendItems, mapRef, setShowAllLayers, setSelectedLegendFilters]);

  // ─── Individual item checkbox ────────────────────────────────────────────

  const handleItemCheckboxChange = useCallback((item, checked, e) => {
    e.stopPropagation();
    userHasInteracted.current = true;

    setSelectedLegendFilters(prev => {
      let next;
      if (checked) {
        const alreadyIn = prev.some(f => f.type === item.type && f.label === item.baseLabel);
        next = alreadyIn ? prev : [...prev, { type: item.type, label: item.baseLabel }];
      } else {
        next = prev.filter(f => !(f.type === item.type && f.label === item.baseLabel));
      }

      // Auto-sync "Show All" based on whether every toggleable item is now selected
      const allChecked = toggleableItems.every(ti =>
        next.some(f => f.type === ti.type && f.label === ti.baseLabel)
      );
      setShowAllLayers(allChecked);

      return next;
    });
  }, [toggleableItems, setShowAllLayers, setSelectedLegendFilters]);

  // ─── Checked state — selectedLegendFilters is the single source of truth ─

  const isItemChecked = useCallback((item) => {
    // Always-visible disasters are always checked and disabled
    if (item.type === 'incident' && ALWAYS_VISIBLE_DISASTERS.includes(item.baseLabel)) {
      return true;
    }
    return selectedLegendFilters.some(
      f => f.type === item.type && f.label === item.baseLabel
    );
  }, [selectedLegendFilters]);

  if (legendItems.length === 0) return null;

  return (
    <div
      style={{
        position: "absolute", top: `${topOffset}px`, left: "50px", zIndex: 1000,
        backgroundColor: "rgba(255, 255, 255, 0.95)", backdropFilter: "blur(10px)",
        borderRadius: "8px", boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
        border: "1px solid rgba(0,0,0,0.1)",
        minWidth: effectiveExpanded ? "220px" : "auto", maxWidth: "250px",
        maxHeight: "450px", overflow: "hidden", transition: "all 0.3s ease, top 0.2s ease",
        fontFamily: "'Segoe UI', Arial, sans-serif",
        opacity: isInfoWindowOpen ? 0.3 : 1,
        pointerEvents: isInfoWindowOpen ? "none" : "auto",
      }}
    >
      {/* Header — ONLY STYLE CHANGE: navy gradient replaces teal #18a3a0 */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 12px",
          background: "linear-gradient(135deg, #0f2847 0%, #1a3f6f 50%, #245a8d 100%)",
          color: "white",
          cursor: "pointer", borderRadius: effectiveExpanded ? "8px 8px 0 0" : "8px",
          fontSize: "13px", fontWeight: "600", userSelect: "none",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          🗺️ Legend
          <span style={{
            backgroundColor: "rgba(255,255,255,0.25)", borderRadius: "10px",
            padding: "2px 6px", fontSize: "10px", fontWeight: "500"
          }}>
            {legendItems.length}
          </span>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {effectiveExpanded && activeItem && (
            <span style={{ fontSize: "11px", opacity: 0.9,marginLeft:"-20px" }}><Search color="white" size={13} /></span>
          )}
          {effectiveExpanded && !activeItem && (
            <span
              title="Click any label to zoom to that layer"
              style={{ fontSize: "11px", opacity: 0.9, cursor: "default", userSelect: "none",marginLeft:"-20px" }}
            ><Search color="white" size={13} /></span>
          )}
          {effectiveExpanded ? <FaChevronDown size={12} /> : <FaChevronUp size={12} />}
        </div>
      </div>

      {/* Items */}
      {effectiveExpanded && (
        <div style={{ padding: "10px", overflowY: "auto", maxHeight: "380px" }}>

          {/* "Show All" Checkbox */}
          <div style={{
            display: "flex", alignItems: "center", gap: "8px", padding: "6px 8px",
            marginBottom: "8px", borderRadius: "6px",
            backgroundColor: "#f3f4f6", border: "1.5px solid #d1d5db"
          }}>
            <input
              type="checkbox"
              checked={showAllLayers}
              onChange={(e) => handleAllCheckboxChange(e.target.checked)}
              style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#18a3a0" }}
            />
            <span style={{ fontSize: "13px", fontWeight: "600", color: "#1f2937" }}>Show All</span>
          </div>

          {/* Individual Items */}
          {legendItems.map((item) => {
            const hasPositions = item.positions && item.positions.length > 0;
            const isActive = activeItem === item.key;
            const isAlwaysVisible =
              item.type === 'incident' && ALWAYS_VISIBLE_DISASTERS.includes(item.baseLabel);
            const isChecked = isItemChecked(item);

            return (
              <div
                key={item.key}
                style={{
                  display: "flex", alignItems: "center", gap: "8px", padding: "6px 8px",
                  borderRadius: "5px", marginBottom: "4px", transition: "all 0.2s ease",
                  backgroundColor: isActive ? `${item.borderColor || item.color}15` : "transparent",
                  border: isActive
                    ? `1.5px solid ${item.borderColor || item.color}40`
                    : "1.5px solid transparent",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isActive
                    ? `${item.borderColor || item.color}20` : "#f9fafb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isActive
                    ? `${item.borderColor || item.color}15` : "transparent";
                }}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isAlwaysVisible}
                  onChange={(e) => handleItemCheckboxChange(item, e.target.checked, e)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: "16px", height: "16px",
                    cursor: isAlwaysVisible ? "not-allowed" : "pointer",
                    accentColor: item.borderColor || item.color,
                    opacity: isAlwaysVisible ? 0.6 : 1,
                  }}
                  title={
                    isAlwaysVisible
                      ? "Always visible — cannot be toggled"
                      : "Click to toggle visibility"
                  }
                />

                {/* Icon */}
                {item.isImage ? (
                  <div style={{
                    width: "26px", height: "26px", borderRadius: "50%",
                    border: `2px solid ${item.borderColor}`,
                    backgroundColor: item.bgColor || item.color,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "14px", flexShrink: 0,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}>
                    {item.emoji || "🏢"}
                  </div>
                ) : (
                  <div style={{
                    width: "26px", height: "26px", borderRadius: "50%",
                    border: item.type === 'incident' || item.key.startsWith('incident-')
                      ? `2px solid #DC2626`
                      : `2px solid ${item.borderColor}`,
                    backgroundColor: item.bgColor || item.color,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "14px", flexShrink: 0,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}>
                    {item.emoji}
                  </div>
                )}

                {/* Label */}
                <span
                  onClick={() => hasPositions && handleLegendItemClick(item)}
                  style={{
                    fontSize: "12px", color: "#374151", fontWeight: "500",
                    lineHeight: "1.3", flex: 1,
                    cursor: hasPositions ? "pointer" : "default",
                  }}
                  title={hasPositions ? `Click to zoom to ${item.label}` : item.label}
                >
                  {item.label}
                </span>
                {/* {isComputingCounts ? (
                  <div style={{
                    width: "24px", height: "16px", borderRadius: "4px",
                    background: "#e5e7eb", animation: "pulse 1.4s ease-in-out infinite",
                    flexShrink: 0,
                  }} />
                ) : (
                  item.positions?.length > 0 && (
                    <span style={{
                      fontSize: "11px",
                      background: item.borderColor || item.color || "#3b82f6",
                      color: "white", borderRadius: "10px",
                      padding: "1px 7px", fontWeight: "600", flexShrink: 0,
                    }}>
                      {item.positions.length}
                    </span>
                  )
                )} */}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActivePanelMapLegend;